type Zone = "before" | "after" | "inside";

const ZONE_CLASSES = ["drop-before", "drop-after", "drop-inside"];

/** Drag a page onto another: top quarter = before, bottom quarter = after, middle = inside. */
export class P9Tree extends HTMLElement {
  #dragged: HTMLLIElement | null = null;
  #target: { li: HTMLLIElement; zone: Zone } | null = null;

  connectedCallback(): void {
    this.addEventListener("dragstart", (event) => {
      const li = (event.target as HTMLElement).closest<HTMLLIElement>("li[data-id]");
      if (!li || !event.dataTransfer) return;
      this.#dragged = li;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", li.dataset.id!);
      li.classList.add("dragging");
    });

    this.addEventListener("dragover", (event) => {
      const row = (event.target as HTMLElement).closest<HTMLElement>("a, summary");
      const li = row?.closest<HTMLLIElement>("li[data-id]");
      this.#clear();
      if (!row || !li || !this.#dragged || this.#dragged.contains(li)) return;
      // Pages shown under "Not shared with you" really live elsewhere; dropping next to them would misplace a page.
      if (li.closest(".tree-hidden")) return;
      event.preventDefault();
      const rect = row.getBoundingClientRect();
      const y = (event.clientY - rect.top) / rect.height;
      const zone: Zone = y < 0.25 ? "before" : y > 0.75 ? "after" : "inside";
      li.classList.add(`drop-${zone}`);
      this.#target = { li, zone };
    });

    this.addEventListener("dragleave", (event) => {
      if (!this.contains(event.relatedTarget as Node)) this.#clear();
    });

    this.addEventListener("drop", (event) => {
      event.preventDefault();
      const target = this.#target;
      const dragged = this.#dragged;
      this.#clear();
      if (target && dragged) void this.#move(dragged, target.li, target.zone);
    });

    this.addEventListener("dragend", () => {
      this.#dragged?.classList.remove("dragging");
      this.#dragged = null;
      this.#clear();
    });
  }

  #clear(): void {
    this.#target?.li.classList.remove(...ZONE_CLASSES);
    this.#target = null;
    this.querySelectorAll(".drop-before, .drop-after, .drop-inside").forEach((el) => el.classList.remove(...ZONE_CLASSES));
  }

  async #move(dragged: HTMLLIElement, target: HTMLLIElement, zone: Zone): Promise<void> {
    let parentId: string;
    let index: number;
    if (zone === "inside") {
      parentId = target.dataset.id!;
      index = 1_000_000;
    } else {
      const list = target.parentElement!;
      parentId = list.dataset.parent ?? "";
      const siblings = [...list.children].filter((el) => el !== dragged);
      index = siblings.indexOf(target) + (zone === "after" ? 1 : 0);
    }

    const url = this.dataset.moveUrl!.replace("__ID__", dragged.dataset.id!);
    const body = new URLSearchParams({ parentId, index: String(index) });
    const response = await fetch(url, {
      method: "POST",
      headers: { RequestVerificationToken: antiforgeryToken() },
      body,
    });
    if (response.ok) {
      window.location.reload();
    } else {
      const result = (await response.json().catch(() => ({}))) as { error?: string };
      window.alert(result.error ?? "The page couldn't be moved.");
    }
  }
}

function antiforgeryToken(): string {
  return document.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]')?.value ?? "";
}

customElements.define("p9-tree", P9Tree);
